import Minze, { MinzeElement, Reactive, EventListeners } from 'minze'
import { confirmPasswordReset } from 'firebase/auth'
import { PASSWORD_REGEX, escapeHtml, unescapeHtml } from '../../modules/utils'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'
import { Locale } from '../../modules/locale'
import { Callback } from '../../modules/callback'
import { Font } from '../../constants'

Locale.getInstance().registerKeySet('fr', 'resetPassword', {
  title: 'Réinitialiser le mot de passe',
  password: 'Mot de passe',
  passwordPlaceholder: 'Saisir le mot de passe',
  confirmPassword: 'Confirmer le mot de passe',
  confirmPasswordPlaceholder: 'Confirmer le mot de passe',
  description:
    'Votre mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre',
  errors: {
    passwordCriterias:
      'Votre mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial',
    passwordMatch: 'Les mots de passe ne correspondent pas',
    passwordLength: 'Le mot de passe doit contenir au moins 8 caractères',
    passwordRequired: 'Veuillez saisir votre mot de passe',
    misc: "Quelque chose s'est mal passé",
  },
})

Locale.getInstance().registerKeySet('en', 'resetPassword', {
  title: 'Reset password',
  password: 'Password',
  passwordPlaceholder: 'Enter your password',
  confirmPassword: 'Confirm password',
  confirmPasswordPlaceholder: 'Confirm your password',
  description:
    'Your password must contain at least 8 characters, one uppercase, one lowercase and one digit',
  errors: {
    passwordCriterias:
      'Your password must have at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character',
    passwordMatch: 'Passwords do not match',
    passwordLength: 'Password must contain at least 8 characters',
    passwordRequired: 'Please enter your password',
    misc: 'Something went wrong',
  },
})

export interface LvcResetPasswordContent {
  password: string
  confirmPassword: string
  passwordLengthValid: boolean
  passwordUpperCaseValid: boolean
  passwordLowerCaseValid: boolean
  passwordNumberValid: boolean
  passwordSpecialCharValid: boolean
}

export class LvcResetPasswordContent extends MinzeElement {
  reactive: Reactive = [
    ['password', ''],
    ['confirmPassword', ''],
    ['isCreateMode', false],
    ['passwordLengthValid', false],
    ['passwordUpperCaseValid', false],
    ['passwordLowerCaseValid', false],
    ['passwordNumberValid', false],
    ['passwordSpecialCharValid', false],
    ['tooltipOpened', false],
    ['allPasswordCriteriasMet', true],
  ]

  handleInputCb = Callback.attach(this.handleInput.bind(this))

  errors: any = {
    password: false,
    confirmPassword: false,
  }

  onReady(): void | Promise<void> {
    const mainModule = MainModule.getInstance()
    const userData = mainModule.getAuth().getUserData()

    const createPasswordParam = new URLSearchParams(window.location.search).get('createPassword')
    if (createPasswordParam && createPasswordParam === 'true') this.isCreateMode = true

    if (userData && userData.profile) {
    }
  }

  save = async () => {
    const passwordElement = this.select('[name="password"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    const password = passwordElement.value
    const confirmPassword = confirmPasswordElement.value
    this.password = unescapeHtml(password)
    this.confirmPassword = unescapeHtml(confirmPassword)

    for (const key in this.errors) {
      this.errors[key] = false
    }
    if (!this.password) {
      this.errors.password = Locale.getKey('resetPassword.errors.passwordRequired')
    }

    if (this.password && (this.password as string).length < 8) {
      this.errors.password = Locale.getKey('resetPassword.errors.passwordLength')
    }
    if (this.password && !PASSWORD_REGEX.test(this.password as string)) {
      this.errors.password = Locale.getKey('resetPassword.errors.passwordCriterias')
    }
    if (this.password !== this.confirmPassword) {
      this.errors.confirmPassword = Locale.getKey('resetPassword.errors.passwordMatch')
    }

    let hasError = false
    for (const key in this.errors) {
      if (this.errors[key]) {
        hasError = true
        break
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth().getFirebaseAuth()
    try {
      let oobCode = new URLSearchParams(window.location.search).get('oobCode')
      await confirmPasswordReset(auth, oobCode as string, this.password as string)
      const language = Locale.language
      const redirectRoute = this.isCreateMode
        ? `${Routing.ROUTES[language].login}?create-password-success=true`
        : `${Routing.ROUTES[language].login}?recover-password-success=true`
      mainModule.getRouting().goTo(redirectRoute)
    } catch (error) {
      this.errors.password = Locale.getKey('resetPassword.errors.misc')
    }

    this.rerender()
  }

  openTooltip() {
    this.tooltipOpened = true
  }

  closeTooltip() {
    this.tooltipOpened = false
    const tooltip = this.select('#password-tooltip')
    tooltip?.classList.remove('open')
  }

  handleInput(event: Event) {
    const passwordCriteriaList = this.selectAll('#password-tooltip li')
    const passwordInput = this.select('[name="password"]')

    if (!passwordInput) {
      console.error('Password input not found')
      return
    }

    const value = unescapeHtml((passwordInput as HTMLInputElement).value)

    this.valuePasswordInput = value

    let allCriteriaMet = true // Assume all criteria are met initially

    passwordCriteriaList?.forEach((criteria, index) => {
      const isCriteriaMet = this.checkPasswordCriteria(value, index + 1)

      switch (index + 1) {
        case 1:
          this.passwordLengthValid = isCriteriaMet
          break
        case 2:
          this.passwordUpperCaseValid = isCriteriaMet
          break
        case 3:
          this.passwordLowerCaseValid = isCriteriaMet
          break
        case 4:
          this.passwordNumberValid = isCriteriaMet
          break
        case 5:
          this.passwordSpecialCharValid = isCriteriaMet
          break
      }

      criteria.classList.toggle('valid', isCriteriaMet)
      criteria.classList.toggle('invalid', !isCriteriaMet)

      if (!isCriteriaMet) {
        allCriteriaMet = false
      }
    })

    // set the allPasswordCriteriasMet to the value of allCriteriaMet
    // so we know if all criterias are met
    this.allPasswordCriteriasMet = allCriteriaMet

    if (
      this.passwordLengthValid &&
      this.passwordUpperCaseValid &&
      this.passwordLowerCaseValid &&
      this.passwordNumberValid &&
      this.passwordSpecialCharValid
    ) {
      setTimeout(() => {
        this.closeTooltip()
      }, 1000)
    } else {
      if (!this.tooltipOpened) this.openTooltip()
    }

    this.rerender()
  }

  checkPasswordCriteria(password: string, criteriaIndex: number): boolean {
    switch (criteriaIndex) {
      case 1:
        return password.length >= 8
      case 2:
        return /[A-Z]/.test(password)
      case 3:
        return /[a-z]/.test(password)
      case 4:
        return /\d/.test(password)
      case 5:
        return /[!@#$%^&*(),.?":{}|<>]/.test(password)
      default:
        return false
    }
  }

  renderPasswordCriteria(pointKey: string, isValid: boolean): string {
    if (this.password.length === 0) {
      return `<li class="password-criteria">
        <lvc-blue-x-icon></lvc-blue-x-icon>
        ${Locale.getKey(pointKey)}
      </li>`
    }

    return `
      <li class="password-criteria ${isValid ? 'valid' : 'invalid'}">
        ${isValid ? `<lvc-green-checkmark-icon></lvc-green-checkmark-icon>` : `<lvc-red-x-icon></lvc-red-x-icon>`}
        ${Locale.getKey(pointKey)}
      </li>
    `
  }

  html = () => `
    <div class="form">
      <lvc-box mt="24px">
         <lvc-input id="password" name="password" type="password" error="${this.errors.password}" placeholder="${Locale.getKey('resetPassword.passwordPlaceholder')}" default-value="${escapeHtml(this.password)}" label="${Locale.getKey('resetPassword.password')}" on-change="${this.handleInputCb}"></lvc-input>
      </lvc-box>
      <dialog id="password-tooltip" class="tooltip ${this.tooltipOpened ? `open` : ``}">
        <lvc-pointy-popup-arrow></lvc-pointy-popup-arrow>
        <div class="content-dialog">
          <div class="password-tooltip-content" slot="tooltip-content">
            <h3>${Locale.getKey('signup.password.tooltip.title')}</h3>
            <ul class="password-tooltip">
              ${this.renderPasswordCriteria('signup.password.tooltip.point1', this.passwordLengthValid)}
              ${this.renderPasswordCriteria('signup.password.tooltip.point2', this.passwordUpperCaseValid)}
              ${this.renderPasswordCriteria('signup.password.tooltip.point3', this.passwordLowerCaseValid)} 
              ${this.renderPasswordCriteria('signup.password.tooltip.point4', this.passwordNumberValid)}
              ${this.renderPasswordCriteria('signup.password.tooltip.point5', this.passwordSpecialCharValid)}
            </ul>
          </div>
        </div>
      </dialog>
      <lvc-box mt="12px">
         <lvc-input id="confirmPassword" name="confirmPassword" type="password" error="${this.errors.confirmPassword}" placeholder="${Locale.getKey('resetPassword.confirmPasswordPlaceholder')}" default-value="${escapeHtml(this.confirmPassword)}" label="${Locale.getKey('resetPassword.confirmPassword')}"></lvc-input>
      </lvc-box>
      <lvc-box 
        display="${window.matchMedia('(max-width: 730px)').matches ? 'block' : 'flex'}" 
        mt="${window.matchMedia('(max-width: 730px)').matches ? '24px' : '32px'}" 
        justifyContent="center"
      >
        <lvc-button enable-hover-box-shadow="true" 
          name="save" 
          width="${window.matchMedia('(max-width: 730px)').matches ? '100%' : 'fit-content'}"
          padding="0.5rem 1rem"
          font-size="1rem"
        >
          ${Locale.getKey('general.modify')}
        </lvc-button>
      </lvc-box>  
    </div>
  `

  css = () => `
    :host {
      
    }
    .privacy-link{
      font-style: normal;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      text-decoration: underline;
      color: #1E007D;
    }

    .privacy-link:hover {
      color: #FFF;
    }
    
    a, a:visited {
      font-style: normal;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      text-decoration: none;
      color: #1E007D;
    }

    .content-dialog{
      display:flex;

    }

    .urls{
      font-style: normal;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      text-decoration: underline;
      color: #1E007D;
    }

    a>u:hover, .urls:hover {
      color: #FFF;
    }
    
    .password-tooltip-content h3 {
      margin: 0 0 8px;
    }
    
    .password-tooltip-content ul {
      margin: 0;

    }

    lvc-pointy-popup-arrow{
      z-index: 1;
      position: absolute;
      top: -11px;
      right: 10px;
    }

    .tooltip.open {
      margin-top: 15px;
      display: block;
      z-index: 1;
      border: 1px solid rgba(30, 0, 125, 0.3);
      box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
      border-radius: 8px;
      padding: 16px;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      color: #1E007D;
      max-width: 90vw;
      width: 340px;
      margin-left: auto; 
      margin-right: 0;

    }

    .password-tooltip-content ul {
      margin: 0;
      padding: 0;
      list-style: none; /* Remove default list style */
    }

    .password-tooltip-content li {
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      letter-spacing: 0px;
      text-align: left;
      
    }
    
    .password-tooltip-content li::before {
      content: ''; /* Empty content to remove default bullet point */
      display: inline-block;
      width: 9px;
     // background-color: red;
      height: 10px;
      margin-right: 8px;
     // background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none" stroke="%231E007D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 9C6.72234 7.77224 5.49206 6.47963 4.31317 5.1268M4.31317 5.1268C3.16768 3.81229 2.04474 2.42401 1 1M4.31317 5.1268C5.39654 3.6513 6.68118 2.41964 8 1M4.31317 5.1268C3.06821 6.63983 2.15598 7.52691 1 9" /></svg>') no-repeat center center;
    }

    .password-criteria {
      display:flex;
      align-items: center;
      margin-top: 8px;
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      color: #1E007D;
    }

    lvc-blue-x-icon, lvc-green-checkmark-icon, lvc-red-x-icon {
      margin-right: 10px;
    }

    .password-criteria.valid {
      color: green;
    }

    .password-tooltip-content li.valid {
      color: green;
    }

    .password-criteria.invalid {
      color: red;
    }
    
    label {
      font-family: inherit;
      color: #1E007D;
      font-size: 14px;
      line-height: 17px;
      font-weight: ${Font.weight.bold};
      margin-bottom: 4px;
    }
  `

  eventListeners: EventListeners = [
    ['[name="save"]', 'click', this.save],
    ['[name="password"]', 'focus', this.openTooltip.bind(this)],
    ['[name="password"]', 'blur', this.closeTooltip.bind(this)],
  ]
}
